import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChefHat, MapPin } from "lucide-react";
import { SAUDI_CITIES } from "@/data/saudi_locations";
import { ImageUpload } from "./ImageUpload";
import { CUISINES, emptyMenuItem } from "./constants";

export function RestaurantForm({ editing, setEditing }) {
  const set = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));
  const setMenu = (idx, k, v) => setEditing((prev) => ({
    ...prev,
    menu: prev.menu.map((m, i) => i === idx ? { ...m, [k]: v } : m),
  }));
  const addMenuItem = () => setEditing((prev) => ({ ...prev, menu: [...prev.menu, { ...emptyMenuItem }] }));
  const removeMenuItem = (idx) => setEditing((prev) => ({ ...prev, menu: prev.menu.filter((_, i) => i !== idx) }));

  const toggleCity = (cid) => setEditing((prev) => {
    const cur = prev.cities || [];
    return { ...prev, cities: cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid] };
  });
  const toggleAllCities = () => setEditing((prev) => {
    const allIds = SAUDI_CITIES.map((c) => c.id);
    const cur = prev.cities || [];
    return { ...prev, cities: cur.length === allIds.length ? [] : allIds };
  });

  return (
    <div className="space-y-5 py-2">
      {/* Basic info */}
      <section>
        <h3 className="font-bold text-sm mb-3">المعلومات الأساسية</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>الاسم (عربي)</Label>
            <Input value={editing.name_ar} onChange={(e) => set("name_ar", e.target.value)} data-testid="form-name-ar" />
          </div>
          <div>
            <Label>Name (English)</Label>
            <Input value={editing.name_en} onChange={(e) => set("name_en", e.target.value)} data-testid="form-name-en" />
          </div>
          <div>
            <Label>الصنف</Label>
            <Select value={editing.cuisine_en} onValueChange={(v) => set("cuisine_en", v)}>
              <SelectTrigger data-testid="form-cuisine"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CUISINES.map((c) => <SelectItem key={c.id} value={c.id}>{c.ar}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>التقييم (0-5)</Label>
            <Input type="number" step="0.1" min="0" max="5" value={editing.rating} onChange={(e) => set("rating", e.target.value)} />
          </div>
          <div>
            <Label>وقت التوصيل (مثلاً 25-35)</Label>
            <Input value={editing.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} />
          </div>
          <div>
            <Label>رسوم التوصيل (SAR)</Label>
            <Input type="number" value={editing.delivery_fee} onChange={(e) => set("delivery_fee", e.target.value)} />
          </div>
          <div>
            <Label>الحد الأدنى (SAR)</Label>
            <Input type="number" value={editing.min_order} onChange={(e) => set("min_order", e.target.value)} />
          </div>
        </div>
      </section>

      {/* Images */}
      <section>
        <h3 className="font-bold text-sm mb-3">صور المطعم (رفع من الجهاز)</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <ImageUpload label="صورة المطعم (Thumbnail)" value={editing.image} onChange={(v) => set("image", v)} testId="form-image-upload" />
          <ImageUpload label="صورة الغلاف (Cover)" wide value={editing.cover_image} onChange={(v) => set("cover_image", v)} testId="form-cover-upload" />
        </div>
      </section>

      {/* Descriptions */}
      <section>
        <h3 className="font-bold text-sm mb-3">الوصف</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>الوصف (عربي)</Label>
            <Textarea rows={2} value={editing.description_ar} onChange={(e) => set("description_ar", e.target.value)} />
          </div>
          <div>
            <Label>Description (English)</Label>
            <Textarea rows={2} value={editing.description_en} onChange={(e) => set("description_en", e.target.value)} />
          </div>
        </div>
      </section>

      {/* Cities coverage */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#E05D3A]" />
            المدن المغطاة ({editing.cities?.length || 0})
          </h3>
          <button
            type="button"
            onClick={toggleAllCities}
            className="text-xs font-semibold text-[#E05D3A] hover:underline"
            data-testid="toggle-all-cities"
          >
            {editing.cities?.length === SAUDI_CITIES.length ? "إلغاء الكل" : "اختر الكل (جميع السعودية)"}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto p-1">
          {SAUDI_CITIES.map((c) => {
            const selected = (editing.cities || []).includes(c.id);
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => toggleCity(c.id)}
                className={`px-3 py-2 rounded-xl text-xs font-medium border text-start transition-all ${selected ? "bg-[#E05D3A] text-white border-[#E05D3A]" : "bg-white text-[#1A1A1A] border-[#EAEAEA] hover:border-[#E05D3A]"}`}
                data-testid={`city-chip-${c.id}`}
              >
                {c.name_ar}
              </button>
            );
          })}
        </div>
        {(editing.cities?.length || 0) === 0 && (
          <p className="text-xs text-amber-700 mt-2">⚠️ لو ما اخترت أي مدينة، المطعم بيظهر في كل المدن كافتراضي.</p>
        )}
      </section>

      {/* Menu items */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-[#E05D3A]" />
            قائمة الطعام ({editing.menu.length})
          </h3>
          <Button size="sm" variant="outline" onClick={addMenuItem} className="rounded-full" data-testid="form-add-menu-item">
            <Plus className="w-3.5 h-3.5 me-1" /> إضافة صنف
          </Button>
        </div>
        <div className="space-y-3">
          {editing.menu.map((item, idx) => (
            <div key={item.menu_item_id || item._k || `menu-${idx}`} className="jh-card p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#6B6B6B]">صنف #{idx + 1}</span>
                <button onClick={() => removeMenuItem(idx)} className="text-red-600 text-xs hover:underline">حذف</button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="اسم (عربي)" value={item.name_ar} onChange={(e) => setMenu(idx, "name_ar", e.target.value)} />
                <Input placeholder="Name (English)" value={item.name_en} onChange={(e) => setMenu(idx, "name_en", e.target.value)} />
                <Input placeholder="فئة (مثلاً Burgers)" value={item.category} onChange={(e) => setMenu(idx, "category", e.target.value)} />
                <Input type="number" placeholder="السعر (SAR)" value={item.price} onChange={(e) => setMenu(idx, "price", e.target.value)} />
                <Input placeholder="وصف (عربي)" value={item.description_ar} onChange={(e) => setMenu(idx, "description_ar", e.target.value)} />
                <Input placeholder="Description (EN)" value={item.description_en} onChange={(e) => setMenu(idx, "description_en", e.target.value)} />
                <div className="sm:col-span-2">
                  <ImageUpload label="صورة الصنف" value={item.image} onChange={(v) => setMenu(idx, "image", v)} testId={`menu-image-upload-${idx}`} />
                </div>
              </div>
            </div>
          ))}
          {editing.menu.length === 0 && (
            <div className="text-center py-6 text-sm text-[#6B6B6B] border-2 border-dashed border-[#EAEAEA] rounded-xl">
              لا توجد أصناف بعد. اضغط &quot;إضافة صنف&quot; لإضافة أول طبق.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
